import React, { useEffect, useRef, useState } from "react";
import { Page, PageHeader } from "../components/PageHeader";
import { api, extractError } from "../lib/api";
import { fmtCLP, fmtDate } from "../lib/format";
import { usePermissions } from "../hooks/usePermissions";
import { useReadOnly } from "../hooks/useReadOnly";
import { toast } from "sonner";
import {
  Inbox, RefreshCw, Upload, Download, FileCode, Copy, AlertTriangle, CheckCircle2, MailCheck, Reply,
  Landmark, BookOpen, Scale,
} from "lucide-react";
import { Link } from "react-router-dom";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle,
} from "../components/ui/dialog";

const inputCls = "w-full px-3 py-2 border border-border rounded-sm text-sm bg-background";
const CARD = "bg-card border border-border rounded-md";
const BTN_PRIMARY = "inline-flex items-center justify-center gap-1.5 bg-primary hover:bg-success text-primary-foreground text-sm font-medium px-3 py-2 rounded-sm disabled:opacity-60";
const BTN_GHOST = "inline-flex items-center gap-1 border border-border hover:bg-muted text-foreground rounded-sm px-2.5 py-1.5 text-[13px] disabled:opacity-60";
const BADGE = "inline-flex items-center px-2 py-0.5 rounded-sm text-[11px] font-medium";

const TIPOS = {
  33: "Factura", 34: "Factura exenta", 43: "Liquidación factura", 46: "Factura de compra", 52: "Guía de despacho",
  56: "Nota de débito", 61: "Nota de crédito",
};
const TIPOS_RECIBO = [33, 34, 46, 52];
const TIPOS_SII = [33, 34, 43];
const TIPOS_LIBRO = [33, 34, 43, 56, 61];
const ACEPTADOS = ["aceptado", "aceptado_discrepancias", "aceptado_tacito"];
const ESTADO_COMERCIAL = {
  pendiente: ["Pendiente", "bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-200"],
  aceptado: ["Aceptado", "bg-emerald-100 text-emerald-800 dark:bg-emerald-900/40 dark:text-emerald-200"],
  aceptado_discrepancias: ["Aceptado con discrepancias", "bg-sky-100 text-sky-800 dark:bg-sky-900/40 dark:text-sky-200"],
  aceptado_tacito: ["Aceptado (plazo vencido)", "bg-emerald-50 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300"],
  rechazado: ["Rechazado", "bg-red-100 text-red-800 dark:bg-red-900/40 dark:text-red-200"],
};
const ACCIONES_SII = {
  ACD: "Aceptación del contenido",
  ERM: "Recibo de mercaderías",
  RCD: "Reclamo al contenido",
  RFP: "Reclamo por falta parcial de mercaderías",
  RFT: "Reclamo por falta total de mercaderías",
};
const RECLAMOS = ["RCD", "RFP", "RFT"];
const CUADRATURA = {
  coincide: ["Coincide", "bg-emerald-100 text-emerald-800 dark:bg-emerald-900/40 dark:text-emerald-200"],
  diferencia: ["Monto distinto", "bg-red-100 text-red-800 dark:bg-red-900/40 dark:text-red-200"],
  solo_sii: ["Solo en el SII", "bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-200"],
  solo_recibido: ["No está en el RCV", "bg-sky-100 text-sky-800 dark:bg-sky-900/40 dark:text-sky-200"],
};
const cuentasGasto = (cuentas) => cuentas.filter((c) => c.imputable !== false && c.activa !== false
  && (c.tipo === "egreso" || String(c.codigo).startsWith("1.2")));
const normRut = (rut) => {
  const r = String(rut || "").replace(/[^0-9kK]/g, "").toUpperCase();
  return r.length < 2 ? r : `${r.slice(0, -1)}-${r.slice(-1)}`;
};
const ACUSE = {
  enviado: ["Acuse enviado", "bg-emerald-100 text-emerald-800 dark:bg-emerald-900/40 dark:text-emerald-200"],
  sin_destino: ["Acuse para descargar", "bg-muted text-muted-foreground"],
  sin_certificado: ["Sin certificado", "bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-200"],
  error: ["Error al enviar", "bg-red-100 text-red-800 dark:bg-red-900/40 dark:text-red-200"],
};
const RESPUESTA_CLIENTE = {
  recepcion: "Acuse de recibo",
  resultado: "Aprobación comercial",
  recibo: "Recibo de mercaderías",
};
const ESTADO_DTE = { 0: "Aceptado", 1: "Aceptado con discrepancias", 2: "Rechazado" };

const nombreTipo = (t) => TIPOS[t] || `Tipo ${t}`;
const fmtFechaHora = (iso) => (iso ? new Date(iso).toLocaleString("es-CL", { dateStyle: "short", timeStyle: "short" }) : "—");

function diasEntre(desde, hasta) {
  if (!desde || !hasta) return null;
  return Math.round((new Date(`${hasta}T00:00:00`) - new Date(`${desde}T00:00:00`)) / 86400000);
}

async function descargar(path, nombre) {
  try {
    const res = await api.get(path, { responseType: "blob" });
    const url = URL.createObjectURL(res.data);
    const a = document.createElement("a");
    a.href = url; a.download = nombre; a.click();
    setTimeout(() => URL.revokeObjectURL(url), 60000);
  } catch (e) {
    toast.error(extractError(e));
  }
}

async function verPdf(path) {
  try {
    const res = await api.get(path, { responseType: "blob" });
    const url = URL.createObjectURL(res.data);
    window.open(url, "_blank");
    setTimeout(() => URL.revokeObjectURL(url), 60000);
  } catch (e) {
    toast.error(extractError(e));
  }
}

export default function DocumentosRecibidos() {
  const { canWrite } = usePermissions();
  const readOnly = useReadOnly();
  const editable = canWrite("facturador") && !readOnly;
  const [tab, setTab] = useState("pendientes");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [envios, setEnvios] = useState(null);
  const [clientes, setClientes] = useState(null);
  const [responder, setResponder] = useState(null);
  const [libro, setLibro] = useState(null);
  const [siiDoc, setSiiDoc] = useState(null);
  const [cuentas, setCuentas] = useState([]);

  useEffect(() => {
    if (editable) api.get("/cuentas").then(({ data: d }) => setCuentas(cuentasGasto(d || []))).catch(() => {});
  }, [editable]);

  const load = async () => {
    setLoading(true);
    try {
      const { data: d } = await api.get("/facturador/recibidos", { params: { estado: tab === "todos" ? "todos" : "pendientes" } });
      setData(d);
    } catch (e) {
      toast.error(extractError(e));
    } finally {
      setLoading(false);
    }
  };

  const loadEnvios = () => api.get("/facturador/recibidos/envios").then(({ data: d }) => setEnvios(d))
    .catch((e) => toast.error(extractError(e)));
  const loadClientes = () => api.get("/facturador/recibidos/respuestas-clientes").then(({ data: d }) => setClientes(d))
    .catch((e) => toast.error(extractError(e)));

  useEffect(() => {
    if (tab === "envios") loadEnvios();
    else if (tab === "clientes") loadClientes();
    else if (tab !== "cuadratura") load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab]);

  const recargar = () => {
    load();
    if (envios) loadEnvios();
    if (clientes) loadClientes();
  };

  return (
    <Page>
      <PageHeader
        title="Documentos recibidos"
        subtitle="Facturas y otros DTE que sus proveedores le envían al correo de intercambio"
        testid="recibidos-header"
      />
      <BuzonCard data={data} editable={editable} onCambio={recargar} />

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="mb-4">
          <TabsTrigger value="pendientes" data-testid="tab-recibidos-pendientes">
            <Inbox className="h-4 w-4 mr-1.5" /> Por responder
            {data?.pendientes > 0 && (
              <span className="ml-1.5 rounded-full bg-amber-500 text-white text-[10px] px-1.5">{data.pendientes}</span>
            )}
          </TabsTrigger>
          <TabsTrigger value="todos" data-testid="tab-recibidos-todos">Todos</TabsTrigger>
          <TabsTrigger value="envios" data-testid="tab-recibidos-envios">Envíos y acuses</TabsTrigger>
          <TabsTrigger value="clientes" data-testid="tab-recibidos-clientes">Respuestas de clientes</TabsTrigger>
          <TabsTrigger value="cuadratura" data-testid="tab-recibidos-cuadratura">Cuadratura SII</TabsTrigger>
        </TabsList>
        <TabsContent value="pendientes">
          <DocsTabla data={data} loading={loading} editable={editable} onResponder={setResponder}
            onLibro={setLibro} onSii={setSiiDoc} vacio="No hay documentos pendientes de respuesta." />
        </TabsContent>
        <TabsContent value="todos">
          <DocsTabla data={data} loading={loading} editable={editable} onResponder={setResponder}
            onLibro={setLibro} onSii={setSiiDoc} vacio="Aún no se han recibido documentos." />
        </TabsContent>
        <TabsContent value="cuadratura">
          <CuadraturaTab />
        </TabsContent>
        <TabsContent value="envios">
          <EnviosTabla envios={envios} editable={editable} certificado={data?.certificado} onCambio={loadEnvios} />
        </TabsContent>
        <TabsContent value="clientes">
          <ClientesTabla respuestas={clientes} />
        </TabsContent>
      </Tabs>

      <ResponderDialog doc={responder} data={data} cuentas={cuentas} onClose={() => setResponder(null)}
        onListo={() => { setResponder(null); load(); }} />
      <LibroDialog doc={libro} data={data} cuentas={cuentas} onClose={() => setLibro(null)}
        onListo={() => { setLibro(null); load(); }} />
      <SiiDialog doc={siiDoc} data={data} editable={editable} onClose={() => setSiiDoc(null)} onCambio={load} />
    </Page>
  );
}

// ---------------------------------------------------------------------------
// Buzón
// ---------------------------------------------------------------------------
function BuzonCard({ data, editable, onCambio }) {
  const [revisando, setRevisando] = useState(false);
  const [subiendo, setSubiendo] = useState(false);
  const fileRef = useRef(null);
  const buzon = data?.buzon || {};

  const copiar = () => {
    navigator.clipboard?.writeText(buzon.correo).then(() => toast.success("Correo copiado"));
  };

  const revisar = async () => {
    setRevisando(true);
    try {
      const { data: d } = await api.post("/facturador/recibidos/revisar");
      if (d.reciente) toast.info("El buzón se revisó hace menos de un minuto");
      else if (d.ultimo_error) toast.error(`No se pudo revisar el buzón: ${d.ultimo_error}`);
      else toast.success(`Buzón revisado: ${d.mensajes || 0} correo(s) nuevo(s)`);
      onCambio();
    } catch (e) {
      toast.error(extractError(e));
    } finally {
      setRevisando(false);
    }
  };

  const subir = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    setSubiendo(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data: d } = await api.post("/facturador/recibidos/subir", fd);
      for (const r of d.resultados) toast[r.resultado === "ignorado" || r.resultado === "sin_empresa" ? "warning" : "success"](resumenCarga(r));
      onCambio();
    } catch (err) {
      toast.error(extractError(err));
    } finally {
      setSubiendo(false);
    }
  };

  return (
    <div className={`${CARD} p-4 mb-5`} data-testid="buzon-card">
      <div className="flex flex-wrap items-start gap-4 justify-between">
        <div className="space-y-1.5 min-w-[260px] flex-1">
          <div className="flex items-center gap-2 text-sm font-semibold">
            <MailCheck className="h-4 w-4 text-primary" /> Correo de intercambio
          </div>
          {buzon.correo ? (
            <>
              <div className="flex items-center gap-2">
                <code className="font-mono text-sm bg-muted px-2 py-1 rounded-sm" data-testid="buzon-correo">{buzon.correo}</code>
                <button onClick={copiar} className={BTN_GHOST} title="Copiar"><Copy className="h-3.5 w-3.5" /></button>
              </div>
              <p className="text-[12px] text-muted-foreground leading-relaxed max-w-2xl">
                Registre este correo en el SII como <b>correo de intercambio</b> de su empresa (sii.cl → Factura
                electrónica → Sistema de facturación de mercado → Actualización de datos de la empresa). Sus
                proveedores enviarán ahí sus DTE y Aequor los recibirá y acusará recibo automáticamente.
              </p>
            </>
          ) : (
            <p className="text-[12px] text-muted-foreground">
              El correo de intercambio aún no está habilitado en el servidor. Mientras tanto puede subir los XML que le
              lleguen por otro medio.
            </p>
          )}
          {buzon.configurado && (
            <p className="text-[12px] text-muted-foreground">
              Se revisa cada {buzon.intervalo_min} min · Última revisión: {fmtFechaHora(buzon.ultima_revision)}
              {buzon.ultimo_error && <span className="text-destructive"> · Error: {buzon.ultimo_error}</span>}
            </p>
          )}
          {data && !data.certificado && (
            <p className="text-[12px] text-amber-700 dark:text-amber-300 flex items-center gap-1">
              <AlertTriangle className="h-3.5 w-3.5" />
              Sin certificado digital no se pueden firmar los acuses ni las respuestas. Cárguelo en Facturador → Configuración.
            </p>
          )}
        </div>
        {editable && (
          <div className="flex flex-wrap gap-2">
            {buzon.configurado && (
              <button onClick={revisar} disabled={revisando} className={BTN_GHOST} data-testid="buzon-revisar">
                <RefreshCw className={`h-3.5 w-3.5 ${revisando ? "animate-spin" : ""}`} /> Revisar ahora
              </button>
            )}
            <button onClick={() => fileRef.current?.click()} disabled={subiendo} className={BTN_PRIMARY} data-testid="buzon-subir">
              <Upload className="h-4 w-4" /> {subiendo ? "Procesando…" : "Subir XML"}
            </button>
            <input ref={fileRef} type="file" accept=".xml,.zip" className="hidden" onChange={subir} />
          </div>
        )}
      </div>
      {data && (
        <div className="flex gap-6 mt-3 pt-3 border-t border-border text-sm">
          <div><span className="font-semibold">{data.pendientes}</span> <span className="text-muted-foreground">por responder</span></div>
          <div className={data.por_vencer > 0 ? "text-destructive" : ""}>
            <span className="font-semibold">{data.por_vencer}</span>{" "}
            <span className={data.por_vencer > 0 ? "" : "text-muted-foreground"}>con plazo de reclamo por vencer (2 días o menos)</span>
          </div>
        </div>
      )}
    </div>
  );
}

function resumenCarga(r) {
  if (r.resultado === "ignorado") return `${r.archivo}: ${r.detalle}`;
  if (r.resultado === "sin_empresa") return `${r.archivo}: no está dirigido a esta empresa`;
  const e = r.empresas?.[0] || {};
  if (e.resultado === "repetido") return `${r.archivo}: ya había sido cargado`;
  if (r.resultado === "respuesta") return `${r.archivo}: respuesta de cliente registrada (${e.asociados || 0} documento(s) asociado(s))`;
  return `${r.archivo}: ${e.glosa} · ${e.nuevos} documento(s) nuevo(s)`;
}

// ---------------------------------------------------------------------------
// Documentos
// ---------------------------------------------------------------------------
function Plazo({ doc, hoy }) {
  if (doc.estado_comercial !== "pendiente") return <span className="text-muted-foreground">—</span>;
  const dias = diasEntre(hoy, doc.vence_reclamo);
  if (dias === null) return "—";
  if (dias < 0) return <span className="text-muted-foreground" title="Pasado el plazo el documento se entiende aceptado">Vencido</span>;
  const cls = dias <= 2 ? "text-destructive font-semibold" : "";
  return <span className={cls} title={`Plazo para reclamar hasta el ${fmtDate(doc.vence_reclamo)}`}>{dias === 0 ? "Hoy" : `${dias} día${dias === 1 ? "" : "s"}`}</span>;
}

function EstadoSii({ sii }) {
  const ultimo = sii?.ultimo;
  if (!ultimo) return null;
  const ok = ultimo.ok;
  return (
    <div className={`text-[10px] mt-0.5 flex items-center gap-1 ${ok ? "text-emerald-700 dark:text-emerald-300" : "text-destructive"}`}
      title={`${ultimo.glosa || ""}${ultimo.codigo != null ? ` (código ${ultimo.codigo})` : ""}`}>
      <Landmark className="h-3 w-3" />
      SII: {ACCIONES_SII[ultimo.accion] || ultimo.accion} {ok ? "registrado" : "no registrado"}
    </div>
  );
}

function DocsTabla({ data, loading, editable, onResponder, onLibro, onSii, vacio }) {
  const docs = data?.documentos || [];
  return (
    <div className={`${CARD} overflow-hidden`}>
      <div className="overflow-x-auto">
        <table className="acc-table w-full">
          <thead>
            <tr>
              <th>Recibido</th>
              <th>Emisor</th>
              <th>Documento</th>
              <th>Emisión</th>
              <th className="text-right">Total</th>
              <th>Plazo</th>
              <th>Estado</th>
              <th className="text-right">Acciones</th>
            </tr>
          </thead>
          <tbody>
            {loading && <tr><td colSpan={8} className="text-center py-8 text-muted-foreground">Cargando…</td></tr>}
            {!loading && docs.length === 0 && (
              <tr><td colSpan={8} className="text-center py-8 text-muted-foreground">{vacio}</td></tr>
            )}
            {!loading && docs.map((d) => {
              const [label, cls] = ESTADO_COMERCIAL[d.estado_comercial] || [d.estado_comercial, "bg-muted"];
              return (
                <tr key={d.id} data-testid={`recibido-${d.folio}`}>
                  <td className="whitespace-nowrap">{fmtFechaHora(d.recibido_at)}</td>
                  <td className="max-w-[220px]">
                    <div className="truncate font-medium">{d.razon_social_emisor || "—"}</div>
                    <div className="text-[11px] text-muted-foreground font-mono">{d.rut_emisor}</div>
                  </td>
                  <td className="whitespace-nowrap">
                    {nombreTipo(d.tipo)} <span className="font-mono">N° {d.folio}</span>
                    {(!d.timbre_ok || (d.observaciones || []).length > 0) && (
                      <span title={(d.observaciones || []).join("\n") || "Timbre no verificable"}>
                        <AlertTriangle className="inline h-3.5 w-3.5 ml-1 text-amber-500" />
                      </span>
                    )}
                  </td>
                  <td>{fmtDate(d.fch_emis)}</td>
                  <td className="text-right font-mono font-medium">{fmtCLP(d.total)}</td>
                  <td><Plazo doc={d} hoy={data.hoy} /></td>
                  <td>
                    <span className={`${BADGE} ${cls}`}>{label}</span>
                    {d.envio_respuesta?.error && (
                      <div className="text-[10px] text-destructive mt-0.5 max-w-[180px]">{d.envio_respuesta.error}</div>
                    )}
                    {d.envio_respuesta?.enviado && (
                      <div className="text-[10px] text-muted-foreground mt-0.5">Enviada a {d.envio_respuesta.destino}</div>
                    )}
                    <EstadoSii sii={d.sii} />
                    {d.libro_compras?.factura_id && (
                      <Link to="/app/libro-compras" className="text-[10px] mt-0.5 flex items-center gap-1 text-primary hover:underline">
                        <BookOpen className="h-3 w-3" /> En libro de compras
                        {d.libro_compras.asiento_numero ? ` · asiento N° ${d.libro_compras.asiento_numero}` : ""}
                      </Link>
                    )}
                  </td>
                  <td>
                    <div className="flex items-center gap-1.5 justify-end flex-wrap">
                      <button onClick={() => verPdf(`/facturador/recibidos/${d.id}/pdf`)} className={BTN_GHOST} title="Ver PDF">
                        <Download className="h-3.5 w-3.5" /> PDF
                      </button>
                      <button onClick={() => descargar(`/facturador/recibidos/${d.id}/xml`, `DTE_${d.rut_emisor}_T${d.tipo}F${d.folio}.xml`)}
                        className={BTN_GHOST} title="Descargar XML">
                        <FileCode className="h-3.5 w-3.5" /> XML
                      </button>
                      {(d.respuestas || []).map((r) => (
                        <button key={r.id} onClick={() => descargar(`/facturador/recibidos/${d.id}/respuestas/${r.id}/xml`, r.archivo)}
                          className={BTN_GHOST} title={r.archivo}>
                          <FileCode className="h-3.5 w-3.5" /> {r.tipo === "recibo" ? "Recibo" : "Respuesta"}
                        </button>
                      ))}
                      {TIPOS_SII.includes(d.tipo) && (
                        <button onClick={() => onSii(d)} className={BTN_GHOST} title="Estado en el SII" data-testid={`sii-${d.folio}`}>
                          <Landmark className="h-3.5 w-3.5" /> SII
                        </button>
                      )}
                      {editable && ACEPTADOS.includes(d.estado_comercial) && TIPOS_LIBRO.includes(d.tipo)
                        && !d.libro_compras?.factura_id && (
                        <button onClick={() => onLibro(d)} className={BTN_GHOST} data-testid={`libro-${d.folio}`}>
                          <BookOpen className="h-3.5 w-3.5" /> Al libro de compras
                        </button>
                      )}
                      {editable && d.estado_comercial === "pendiente" && (
                        <button onClick={() => onResponder(d)} className={BTN_GHOST} data-testid={`responder-${d.folio}`}>
                          <Reply className="h-3.5 w-3.5" /> Responder
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function CuentaGasto({ cuentas, value, onChange, sugerida }) {
  return (
    <select className={inputCls} value={value} onChange={onChange} data-testid="cuenta-gasto">
      <option value="">Según el tipo de compra (Gastos generales)</option>
      {cuentas.map((c) => (
        <option key={c.id} value={c.id}>{c.codigo} · {c.nombre}{c.id === sugerida ? " (usada antes con este proveedor)" : ""}</option>
      ))}
    </select>
  );
}

function resumenResultado(data) {
  for (const r of data.sii || []) {
    if (r.ok) toast.success(`SII: ${ACCIONES_SII[r.accion] || r.accion} registrada`);
    else toast.error(`SII no registró ${ACCIONES_SII[r.accion]?.toLowerCase() || r.accion}: ${r.glosa}`);
  }
  if (data.libro?.error) toast.error(`No se pasó al libro de compras: ${data.libro.error}`);
  else if (data.libro) toast.success(`Registrado en el libro de compras${data.libro.asiento_numero ? ` con el asiento N° ${data.libro.asiento_numero} (borrador)` : ""}`);
}

function ResponderDialog({ doc, data, cuentas, onClose, onListo }) {
  const [form, setForm] = useState({});
  const [enviando, setEnviando] = useState(false);
  const sugerida = doc ? data?.cuentas_proveedor?.[normRut(doc.rut_emisor)] || "" : "";

  useEffect(() => {
    if (doc) {
      setForm({
        accion: "aceptar", glosa: "", recibo: TIPOS_RECIBO.includes(doc.tipo), recinto: "",
        destino: doc.remitente || doc.correo_emisor || "", enviar: true,
        registrar_sii: TIPOS_SII.includes(doc.tipo) && !!data?.certificado, reclamo: "RCD",
        libro_compras: TIPOS_LIBRO.includes(doc.tipo), cuenta_id: sugerida,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [doc]);

  if (!doc) return null;
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.type === "checkbox" ? e.target.checked : e.target.value }));
  const rechaza = form.accion === "rechazar";
  const pideGlosa = form.accion !== "aceptar";
  const conRecibo = !rechaza && TIPOS_RECIBO.includes(doc.tipo);
  const puedeSii = TIPOS_SII.includes(doc.tipo);
  const puedeLibro = !rechaza && TIPOS_LIBRO.includes(doc.tipo);

  const enviar = async () => {
    if (pideGlosa && !form.glosa.trim()) {
      toast.error(rechaza ? "Indique el motivo del rechazo" : "Describa la discrepancia");
      return;
    }
    if (conRecibo && form.recibo && !form.recinto.trim()) {
      toast.error("Indique el recinto donde se recibieron las mercaderías o servicios");
      return;
    }
    setEnviando(true);
    try {
      const { data: r } = await api.post(`/facturador/recibidos/${doc.id}/responder`, {
        ...form, recibo: conRecibo && form.recibo, registrar_sii: puedeSii && form.registrar_sii,
        libro_compras: puedeLibro && form.libro_compras, cuenta_id: form.cuenta_id || null,
      });
      if (r.enviado) toast.success(`Respuesta firmada y enviada a ${r.destino}`);
      else if (r.error) toast.warning(`Respuesta registrada, pero no se envió: ${r.error}`);
      else toast.success("Respuesta firmada y registrada");
      resumenResultado(r);
      onListo();
    } catch (e) {
      toast.error(extractError(e));
    } finally {
      setEnviando(false);
    }
  };

  return (
    <Dialog open={!!doc} onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>Responder {nombreTipo(doc.tipo).toLowerCase()} N° {doc.folio}</DialogTitle>
        </DialogHeader>
        <div className="space-y-3 text-sm">
          <div className="text-muted-foreground">
            {doc.razon_social_emisor} ({doc.rut_emisor}) · Total {fmtCLP(doc.total)}
          </div>
          <div className="space-y-1.5">
            {[
              ["aceptar", "Aceptar", "Acepta el contenido del documento."],
              ["aceptar_discrepancias", "Aceptar con discrepancias", "Acepta, pero deja constancia de una diferencia."],
              ["rechazar", "Rechazar", "Reclama el contenido del documento (dentro del plazo de 8 días)."],
            ].map(([valor, label, ayuda]) => (
              <label key={valor} className="flex items-start gap-2 cursor-pointer">
                <input type="radio" name="accion" value={valor} checked={form.accion === valor} onChange={set("accion")} className="mt-1" />
                <span><b>{label}</b> <span className="text-muted-foreground text-[12px]">— {ayuda}</span></span>
              </label>
            ))}
          </div>
          {pideGlosa && (
            <div>
              <label className="text-[12px] font-medium">{rechaza ? "Motivo del rechazo" : "Discrepancia"}</label>
              <input className={inputCls} value={form.glosa} onChange={set("glosa")} maxLength={256}
                placeholder={rechaza ? "Ej.: mercadería no solicitada" : "Ej.: diferencia de precio en el ítem 2"} />
            </div>
          )}
          {conRecibo && (
            <div className="rounded-sm border border-border p-3 space-y-2">
              <label className="flex items-start gap-2 cursor-pointer">
                <input type="checkbox" checked={form.recibo} onChange={set("recibo")} className="mt-1" />
                <span>Otorgar recibo de mercaderías o servicios (Ley 19.983)
                  <span className="block text-[11px] text-muted-foreground">Necesario para que la factura pueda cederse (factoring).</span>
                </span>
              </label>
              {form.recibo && (
                <input className={inputCls} value={form.recinto} onChange={set("recinto")} maxLength={80}
                  placeholder="Recinto de recepción (ej.: Bodega central)" />
              )}
            </div>
          )}
          {puedeSii && (
            <div className="rounded-sm border border-border p-3 space-y-2">
              <label className="flex items-start gap-2 cursor-pointer">
                <input type="checkbox" checked={form.registrar_sii} onChange={set("registrar_sii")} className="mt-1"
                  disabled={!data?.certificado} data-testid="registrar-sii" />
                <span>Registrar {rechaza ? "el reclamo" : "la aceptación"} en el SII
                  <span className="block text-[11px] text-muted-foreground">
                    {data?.certificado
                      ? `Registro de reclamos y aceptación del SII (${data?.ambiente === "produccion" ? "producción" : "certificación"}). El certificado debe ser de una persona autorizada en la empresa.`
                      : "Requiere el certificado digital de la empresa."}
                  </span>
                </span>
              </label>
              {rechaza && form.registrar_sii && (
                <select className={inputCls} value={form.reclamo} onChange={set("reclamo")} data-testid="tipo-reclamo">
                  {RECLAMOS.map((r) => <option key={r} value={r}>{ACCIONES_SII[r]}</option>)}
                </select>
              )}
            </div>
          )}
          {puedeLibro && (
            <div className="rounded-sm border border-border p-3 space-y-2">
              <label className="flex items-start gap-2 cursor-pointer">
                <input type="checkbox" checked={form.libro_compras} onChange={set("libro_compras")} className="mt-1" data-testid="libro-compras" />
                <span>Registrar en el libro de compras
                  <span className="block text-[11px] text-muted-foreground">Crea la compra con su IVA crédito fiscal y un asiento en borrador.</span>
                </span>
              </label>
              {form.libro_compras && (
                <CuentaGasto cuentas={cuentas} value={form.cuenta_id} onChange={set("cuenta_id")} sugerida={sugerida} />
              )}
            </div>
          )}
          <div className="space-y-1.5">
            <label className="flex items-center gap-2 cursor-pointer">
              <input type="checkbox" checked={form.enviar} onChange={set("enviar")} />
              Enviar la respuesta firmada al emisor por correo
            </label>
            {form.enviar && (
              <input className={inputCls} type="email" value={form.destino} onChange={set("destino")} placeholder="Correo de intercambio del emisor" />
            )}
          </div>
        </div>
        <DialogFooter>
          <button onClick={onClose} className={BTN_GHOST}>Cancelar</button>
          <button onClick={enviar} disabled={enviando} className={BTN_PRIMARY} data-testid="responder-confirmar">
            <CheckCircle2 className="h-4 w-4" /> {enviando ? "Firmando…" : "Firmar y responder"}
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function LibroDialog({ doc, data, cuentas, onClose, onListo }) {
  const [cuenta, setCuenta] = useState("");
  const [enviando, setEnviando] = useState(false);
  const sugerida = doc ? data?.cuentas_proveedor?.[normRut(doc.rut_emisor)] || "" : "";

  useEffect(() => { if (doc) setCuenta(sugerida); }, [doc, sugerida]);

  if (!doc) return null;
  const confirmar = async () => {
    setEnviando(true);
    try {
      const { data: libro } = await api.post(`/facturador/recibidos/${doc.id}/libro-compras`, { cuenta_id: cuenta || null });
      resumenResultado({ libro });
      onListo();
    } catch (e) {
      toast.error(extractError(e));
    } finally {
      setEnviando(false);
    }
  };

  return (
    <Dialog open={!!doc} onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>Pasar al libro de compras</DialogTitle>
        </DialogHeader>
        <div className="space-y-3 text-sm">
          <div className="text-muted-foreground">
            {nombreTipo(doc.tipo)} N° {doc.folio} · {doc.razon_social_emisor} ({doc.rut_emisor}) · Total {fmtCLP(doc.total)}
          </div>
          <p className="text-[12px] text-muted-foreground">
            Se registra la compra con su IVA crédito fiscal y un asiento en borrador. Si el documento ya estaba en el
            libro (por ejemplo, importado del Registro de Compras del SII) solo se vincula.
          </p>
          <div>
            <label className="text-[12px] font-medium">Cuenta de gasto</label>
            <CuentaGasto cuentas={cuentas} value={cuenta} onChange={(e) => setCuenta(e.target.value)} sugerida={sugerida} />
          </div>
        </div>
        <DialogFooter>
          <button onClick={onClose} className={BTN_GHOST}>Cancelar</button>
          <button onClick={confirmar} disabled={enviando} className={BTN_PRIMARY} data-testid="libro-confirmar">
            <BookOpen className="h-4 w-4" /> {enviando ? "Registrando…" : "Registrar"}
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function SiiDialog({ doc, data, editable, onClose, onCambio }) {
  const [info, setInfo] = useState(null);
  const [busy, setBusy] = useState(null);

  useEffect(() => { setInfo(null); }, [doc]);

  if (!doc) return null;
  const sii = doc.sii || {};
  const eventos = info?.eventos ?? sii.eventos ?? [];
  const recepcion = info?.fecha_recepcion ?? sii.fecha_recepcion;

  const consultar = async () => {
    setBusy("consultar");
    try {
      const { data: r } = await api.post(`/facturador/recibidos/${doc.id}/sii/consultar`);
      setInfo(r);
      if (r.glosa) toast.info(`SII: ${r.glosa}`);
      onCambio();
    } catch (e) {
      toast.error(extractError(e));
    } finally {
      setBusy(null);
    }
  };

  const registrar = async (accion) => {
    if (RECLAMOS.includes(accion) && !window.confirm(`¿Registrar en el SII "${ACCIONES_SII[accion]}"? No se puede deshacer.`)) return;
    setBusy(accion);
    try {
      const { data: r } = await api.post(`/facturador/recibidos/${doc.id}/sii`, { accion });
      resumenResultado({ sii: [r] });
      onCambio();
      onClose();
    } catch (e) {
      toast.error(extractError(e));
    } finally {
      setBusy(null);
    }
  };

  const hechas = sii.acciones || {};
  const acciones = ACEPTADOS.includes(doc.estado_comercial) ? ["ACD", ...(TIPOS_RECIBO.includes(doc.tipo) ? ["ERM"] : [])]
    : doc.estado_comercial === "rechazado" ? RECLAMOS : [];

  return (
    <Dialog open={!!doc} onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>{nombreTipo(doc.tipo)} N° {doc.folio} en el SII</DialogTitle>
        </DialogHeader>
        <div className="space-y-3 text-sm">
          <div className="text-muted-foreground">{doc.razon_social_emisor} ({doc.rut_emisor})</div>
          <div className="grid grid-cols-2 gap-2 text-[13px]">
            <div><span className="text-muted-foreground">Recepción en el SII:</span> {recepcion ? fmtFechaHora(recepcion) : "—"}</div>
            <div><span className="text-muted-foreground">Plazo de reclamo:</span> {fmtDate(info?.vence_reclamo || doc.vence_reclamo)}</div>
          </div>
          <div>
            <div className="text-[12px] font-medium mb-1">Eventos registrados</div>
            {eventos.length === 0 ? (
              <div className="text-[12px] text-muted-foreground">
                {sii.consultado_at || info ? "El documento no tiene eventos en el SII." : "Consulte el SII para ver los eventos."}
              </div>
            ) : (
              <ul className="text-[12px] space-y-1">
                {eventos.map((e, i) => (
                  <li key={i}>
                    <b>{ACCIONES_SII[e.codigo] || e.descripcion || e.codigo}</b>
                    <span className="text-muted-foreground"> · {e.fecha} · {e.responsable}</span>
                  </li>
                ))}
              </ul>
            )}
          </div>
          {(sii.registros || []).length > 0 && (
            <div>
              <div className="text-[12px] font-medium mb-1">Registros desde Aequor</div>
              <ul className="text-[12px] space-y-1">
                {sii.registros.map((r, i) => (
                  <li key={i} className={r.ok ? "" : "text-destructive"}>
                    {ACCIONES_SII[r.accion] || r.accion} · {r.ok ? "registrado" : "no registrado"} · {r.glosa}
                    <span className="text-muted-foreground"> · {fmtFechaHora(r.at)}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}
          {!data?.certificado && (
            <p className="text-[12px] text-amber-700 dark:text-amber-300">Para consultar el SII se necesita el certificado digital de la empresa.</p>
          )}
        </div>
        <DialogFooter className="flex-wrap gap-2">
          {editable && data?.certificado && acciones.filter((a) => !hechas[a]).map((a) => (
            <button key={a} onClick={() => registrar(a)} disabled={!!busy} className={BTN_GHOST}>
              Registrar {ACCIONES_SII[a].toLowerCase()}
            </button>
          ))}
          {editable && data?.certificado && (
            <button onClick={consultar} disabled={!!busy} className={BTN_PRIMARY} data-testid="sii-consultar">
              <RefreshCw className={`h-4 w-4 ${busy === "consultar" ? "animate-spin" : ""}`} /> Consultar SII
            </button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ---------------------------------------------------------------------------
// Cuadratura con el Registro de Compras (RCV)
// ---------------------------------------------------------------------------
function periodoActual() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
}

function CuadraturaTab() {
  const [periodo, setPeriodo] = useState(periodoActual());
  const [res, setRes] = useState(null);
  const [loading, setLoading] = useState(false);
  const [filtro, setFiltro] = useState("");

  useEffect(() => {
    if (!/^\d{4}-\d{2}$/.test(periodo)) return;
    setLoading(true);
    api.get("/facturador/recibidos/cuadratura", { params: { periodo } })
      .then(({ data: d }) => setRes(d))
      .catch((e) => toast.error(extractError(e)))
      .finally(() => setLoading(false));
  }, [periodo]);

  const filas = (res?.filas || []).filter((f) => !filtro || f.estado === filtro);
  return (
    <div className={`${CARD} overflow-hidden`} data-testid="cuadratura">
      <div className="p-4 space-y-3 border-b border-border">
        <div className="flex flex-wrap items-end gap-3">
          <div>
            <label className="text-[12px] font-medium block">Período</label>
            <input type="month" className={inputCls} value={periodo} onChange={(e) => setPeriodo(e.target.value)} data-testid="cuadratura-periodo" />
          </div>
          {res && (
            <div className="flex flex-wrap gap-2">
              {[["", `Todos · ${res.filas.length}`], ...Object.entries(CUADRATURA).map(([k, [l]]) => [k, `${l} · ${res.resumen[k] || 0}`])].map(([k, l]) => (
                <button key={k || "todos"} onClick={() => setFiltro(k)}
                  className={`${BTN_GHOST} ${filtro === k ? "bg-muted font-semibold" : ""}`}>{l}</button>
              ))}
            </div>
          )}
        </div>
        <p className="text-[12px] text-muted-foreground flex items-start gap-1.5">
          <Scale className="h-3.5 w-3.5 mt-0.5 shrink-0" />
          Compara los documentos recibidos por intercambio (por fecha de emisión) con el Registro de Compras del SII
          importado para el período. "Solo en el SII" son documentos que el proveedor declaró pero no llegaron al correo
          de intercambio; "No está en el RCV" son documentos recibidos que el SII aún no registra en el período.
        </p>
        {res && !res.rcv_cargado && (
          <p className="text-[12px] text-amber-700 dark:text-amber-300 flex items-center gap-1">
            <AlertTriangle className="h-3.5 w-3.5" /> No hay Registro de Compras importado para este período.
            Impórtelo desde <Link to="/app/libro-compras" className="underline">Libro de compras</Link>.
          </p>
        )}
      </div>
      <div className="overflow-x-auto">
        <table className="acc-table w-full">
          <thead>
            <tr>
              <th>Proveedor</th>
              <th>Documento</th>
              <th>Emisión</th>
              <th className="text-right">Total XML</th>
              <th className="text-right">Total RCV</th>
              <th>Estado</th>
            </tr>
          </thead>
          <tbody>
            {loading && <tr><td colSpan={6} className="text-center py-8 text-muted-foreground">Cargando…</td></tr>}
            {!loading && filas.length === 0 && (
              <tr><td colSpan={6} className="text-center py-8 text-muted-foreground">Sin documentos para mostrar.</td></tr>
            )}
            {!loading && filas.map((f) => {
              const [label, cls] = CUADRATURA[f.estado];
              return (
                <tr key={`${f.rut}-${f.tipo}-${f.folio}`} data-testid={`cuadra-${f.folio}`}>
                  <td className="max-w-[220px]">
                    <div className="truncate font-medium">{f.razon_social || "—"}</div>
                    <div className="text-[11px] text-muted-foreground font-mono">{f.rut}</div>
                  </td>
                  <td className="whitespace-nowrap">{nombreTipo(f.tipo)} <span className="font-mono">N° {f.folio}</span></td>
                  <td>{fmtDate(f.fecha)}</td>
                  <td className="text-right font-mono">{f.total_xml != null ? fmtCLP(f.total_xml) : "—"}</td>
                  <td className="text-right font-mono">{f.total_sii != null ? fmtCLP(f.total_sii) : "—"}</td>
                  <td>
                    <span className={`${BADGE} ${cls}`}>{label}</span>
                    {f.otro_periodo && (
                      <div className="text-[10px] text-muted-foreground mt-0.5">
                        {f.estado === "solo_recibido" ? "En el RCV de" : "Recibido con emisión en"} {f.otro_periodo}
                      </div>
                    )}
                    {f.estado_comercial && f.estado_comercial !== "pendiente" && (
                      <div className="text-[10px] text-muted-foreground mt-0.5">
                        {ESTADO_COMERCIAL[f.estado_comercial]?.[0]}{f.en_libro ? " · en libro" : ""}
                      </div>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Envíos y acuses
// ---------------------------------------------------------------------------
function EnviosTabla({ envios, editable, certificado, onCambio }) {
  const [busy, setBusy] = useState(null);

  const generarAcuse = async (e) => {
    setBusy(e.id);
    try {
      await api.post(`/facturador/recibidos/envios/${e.id}/acuse`);
      toast.success("Acuse generado: descárguelo y envíelo al emisor");
      onCambio();
    } catch (err) {
      toast.error(extractError(err));
    } finally {
      setBusy(null);
    }
  };

  return (
    <div className={`${CARD} overflow-hidden`}>
      <div className="overflow-x-auto">
        <table className="acc-table w-full">
          <thead>
            <tr>
              <th>Recibido</th>
              <th>Archivo</th>
              <th>Emisor</th>
              <th>Resultado</th>
              <th className="text-right">Documentos</th>
              <th>Acuse de recibo</th>
              <th className="text-right">Acciones</th>
            </tr>
          </thead>
          <tbody>
            {!envios && <tr><td colSpan={7} className="text-center py-8 text-muted-foreground">Cargando…</td></tr>}
            {envios?.length === 0 && (
              <tr><td colSpan={7} className="text-center py-8 text-muted-foreground">Aún no se han recibido envíos.</td></tr>
            )}
            {(envios || []).map((e) => {
              const [label, cls] = ACUSE[e.acuse?.estado] || ["—", "bg-muted"];
              return (
                <tr key={e.id}>
                  <td className="whitespace-nowrap">{fmtFechaHora(e.created_at)}</td>
                  <td className="max-w-[200px] truncate" title={e.nombre}>{e.nombre}
                    <div className="text-[11px] text-muted-foreground">{e.origen === "manual" ? "Carga manual" : e.remitente}</div>
                  </td>
                  <td className="font-mono text-[12px]">{e.rut_emisor}</td>
                  <td className={e.estado === 0 ? "" : "text-destructive"}>{e.glosa}</td>
                  <td className="text-right">{e.nuevos} nuevo(s) de {(e.documentos || []).length}</td>
                  <td>
                    <span className={`${BADGE} ${cls}`}>{label}</span>
                    {e.acuse?.error && <div className="text-[10px] text-destructive mt-0.5 max-w-[180px]">{e.acuse.error}</div>}
                  </td>
                  <td>
                    <div className="flex gap-1.5 justify-end">
                      {e.acuse?.archivo && (
                        <button onClick={() => descargar(`/facturador/recibidos/envios/${e.id}/acuse`, e.acuse.archivo)} className={BTN_GHOST}>
                          <FileCode className="h-3.5 w-3.5" /> Acuse
                        </button>
                      )}
                      {editable && certificado && e.acuse?.estado === "sin_certificado" && (
                        <button onClick={() => generarAcuse(e)} disabled={busy === e.id} className={BTN_GHOST}>
                          Generar acuse
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Respuestas de clientes a documentos emitidos
// ---------------------------------------------------------------------------
function ClientesTabla({ respuestas }) {
  return (
    <div className={`${CARD} overflow-hidden`}>
      <p className="text-[12px] text-muted-foreground px-4 pt-3">
        Acuses, aprobaciones y recibos que sus clientes envían como respuesta a los documentos que usted emite. Se
        asocian automáticamente a los documentos del Facturador.
      </p>
      <div className="overflow-x-auto">
        <table className="acc-table w-full">
          <thead>
            <tr>
              <th>Recibida</th>
              <th>Tipo</th>
              <th>Cliente</th>
              <th>Documentos</th>
              <th>Firma</th>
              <th className="text-right">XML</th>
            </tr>
          </thead>
          <tbody>
            {!respuestas && <tr><td colSpan={6} className="text-center py-8 text-muted-foreground">Cargando…</td></tr>}
            {respuestas?.length === 0 && (
              <tr><td colSpan={6} className="text-center py-8 text-muted-foreground">Aún no hay respuestas de clientes.</td></tr>
            )}
            {(respuestas || []).map((r) => (
              <tr key={r.id}>
                <td className="whitespace-nowrap">{fmtFechaHora(r.created_at)}</td>
                <td>{RESPUESTA_CLIENTE[r.tipo] || r.tipo}</td>
                <td className="font-mono text-[12px]">{r.rut_responde}</td>
                <td>
                  {(r.documentos || []).map((d) => (
                    <div key={`${d.tipo}-${d.folio}`} className="text-[12px]">
                      {nombreTipo(d.tipo)} N° {d.folio}
                      {r.tipo === "resultado" && <> · <b>{ESTADO_DTE[d.estado] ?? d.glosa}</b></>}
                      {r.tipo === "recepcion" && <> · {d.glosa}</>}
                      {r.tipo === "recibo" && d.recinto && <> · {d.recinto}</>}
                      {!d.asociado && <span className="text-muted-foreground"> (no emitido desde Aequor)</span>}
                    </div>
                  ))}
                </td>
                <td>{r.firma_ok ? <span className="text-emerald-600">Válida</span> : <span className="text-destructive">No válida</span>}</td>
                <td className="text-right">
                  <button onClick={() => descargar(`/facturador/recibidos/respuestas-clientes/${r.id}/xml`, r.nombre || "respuesta.xml")} className={BTN_GHOST}>
                    <FileCode className="h-3.5 w-3.5" /> XML
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
